/**
 * Virtual-account categories API (TMMAPP-0005 / TMMAPP-0011, moved to the virtual account by
 * TMMAPI-0145 / MM-D-124; design §7.4 / MM-D-38) — wire types from `src/api/generated/`;
 * normalized into the module's domain type. One INCOME / EXPENSE list per virtual account serves
 * its envelopes and its Unassigned. `GET` answers `{ categories, income, expense }` (the figures
 * are 0108's); `DELETE` archives a category that past allocations still reference (it comes back
 * with `archivedAt` set) and removes an unreferenced one. `taxReportable` (MM-D-127) is "Tax
 * deductible" on an EXPENSE category and "Taxable" on an INCOME one.
 */
import { request } from './client';
import type {
  CategoryFiguresView as WireCategoryFigures,
  CategoryListResponse as WireCategoryList,
  EnvelopeCategoryCreateRequest,
  EnvelopeCategoryPatchRequest,
  EnvelopeCategoryReorderRequest,
  EnvelopeCategoryResponse as WireCategory,
} from './generated';

export type CategoryKind = 'INCOME' | 'EXPENSE';

export interface Category {
  id: number;
  name: string;
  kind: CategoryKind;
  sortOrder: number;
  description: string | null;
  archivedAt: string | null;
  /** MM-D-127: counted in the tax report. */
  taxReportable: boolean;
}

/**
 * A category with its served figures over `from..to` (all time by default; TMMAPI-0145, the
 * Categories tab): in / out / net over its live parts, the bank lines that use it, the envelopes
 * holding those parts and whether Unassigned does. Never added up client-side.
 */
export interface CategoryFigures extends Category {
  inMinor: number;
  outMinor: number;
  netMinor: number;
  lineCount: number;
  envelopeIds: number[];
  unassigned: boolean;
}

/** `GET …/categories?from&to` with the per-kind group totals (served). */
export interface CategoryFiguresList {
  categories: CategoryFigures[];
  incomeNetMinor: number;
  expenseNetMinor: number;
}

export interface CategoryCreateRequest {
  name: string;
  kind: CategoryKind;
  description?: string;
  sortOrder?: number;
  taxReportable?: boolean;
}

/** Only the keys the person changed; every other key is omitted (unchanged). */
export type CategoryPatchRequest = EnvelopeCategoryPatchRequest;

const CATEGORY_KINDS: ReadonlySet<string> = new Set(['INCOME', 'EXPENSE']);

/** A category row; `null` (dropped) with an unknown kind. */
export function normalizeCategory(w: WireCategory | WireCategoryFigures): Category | null {
  if (!CATEGORY_KINDS.has(w.kind)) {
    if (import.meta.env.DEV) console.warn('categories: unknown kind dropped', w.kind);
    return null;
  }
  return {
    id: w.id,
    name: w.name,
    kind: w.kind,
    sortOrder: w.sortOrder,
    description: w.description ?? null,
    archivedAt: w.archivedAt ?? null,
    taxReportable: w.taxReportable,
  };
}

/** A bare array (reorder), or the TMMAPI-0145 list envelope `{ categories: [...] }`. */
export function normalizeCategories(value: readonly WireCategory[] | WireCategoryList): Category[] {
  const rows: readonly (WireCategory | WireCategoryFigures)[] =
    'categories' in value ? value.categories : value;
  return rows.flatMap((row) => {
    const category = normalizeCategory(row);
    return category ? [category] : [];
  });
}

/** The list with its served figures. */
export function normalizeCategoryFigures(w: WireCategoryList): CategoryFiguresList {
  const categories = w.categories.flatMap((row) => {
    const category = normalizeCategory(row);
    if (!category) return [];
    return [
      {
        ...category,
        inMinor: row.inMinor,
        outMinor: row.outMinor,
        netMinor: row.netMinor,
        lineCount: row.lineCount,
        envelopeIds: row.envelopeIds,
        unassigned: row.unassigned ?? false,
      },
    ];
  });
  return {
    categories,
    incomeNetMinor: w.income.netMinor,
    expenseNetMinor: w.expense.netMinor,
  };
}

function required(wire: WireCategory): Category {
  const category = normalizeCategory(wire);
  if (!category) throw new Error('category kind is unknown');
  return category;
}

export const categoriesApi = {
  /** GET /v1/virtual-accounts/{virtualAccountId}/categories — every category, archived included. */
  async list(virtualAccountId: number): Promise<Category[]> {
    return normalizeCategories(
      await request<WireCategoryList>('GET', `/v1/virtual-accounts/${virtualAccountId}/categories`),
    );
  },
  /** GET /v1/virtual-accounts/{virtualAccountId}/categories?from&to — the list with its figures. */
  async listWithFigures(
    virtualAccountId: number,
    window: { from?: string | undefined; to?: string | undefined } = {},
  ): Promise<CategoryFiguresList> {
    return normalizeCategoryFigures(
      await request<WireCategoryList>(
        'GET',
        `/v1/virtual-accounts/${virtualAccountId}/categories`,
        { query: { from: window.from, to: window.to } },
      ),
    );
  },
  /** POST /v1/virtual-accounts/{virtualAccountId}/categories — unset optional keys are omitted. */
  async create(virtualAccountId: number, body: CategoryCreateRequest): Promise<Category> {
    const json: EnvelopeCategoryCreateRequest = { name: body.name, kind: body.kind };
    if (body.description !== undefined) json.description = body.description;
    if (body.sortOrder !== undefined) json.sortOrder = body.sortOrder;
    if (body.taxReportable !== undefined) json.taxReportable = body.taxReportable;
    return required(
      await request<WireCategory>('POST', `/v1/virtual-accounts/${virtualAccountId}/categories`, {
        json,
      }),
    );
  },
  /** PATCH /v1/virtual-accounts/{virtualAccountId}/categories/{categoryId} — the changed keys only. */
  async update(
    virtualAccountId: number,
    categoryId: number,
    body: CategoryPatchRequest,
  ): Promise<Category> {
    return required(
      await request<WireCategory>(
        'PATCH',
        `/v1/virtual-accounts/${virtualAccountId}/categories/${categoryId}`,
        { json: body },
      ),
    );
  },
  /** DELETE /v1/virtual-accounts/{virtualAccountId}/categories/{categoryId} — archives when referenced. */
  async remove(virtualAccountId: number, categoryId: number): Promise<void> {
    await request<unknown>(
      'DELETE',
      `/v1/virtual-accounts/${virtualAccountId}/categories/${categoryId}`,
    );
  },
  /** POST /v1/virtual-accounts/{virtualAccountId}/categories/reorder — no UI; 0108 adopts it. */
  async reorder(
    virtualAccountId: number,
    kind: EnvelopeCategoryReorderRequest['kind'],
    categoryIds: number[],
  ): Promise<Category[]> {
    return normalizeCategories(
      await request<WireCategory[]>(
        'POST',
        `/v1/virtual-accounts/${virtualAccountId}/categories/reorder`,
        { json: { kind, categoryIds } satisfies EnvelopeCategoryReorderRequest },
      ),
    );
  },
};
